import type { Identity } from "./identity";

export class ArtisApiError extends Error {}

export interface WidgetConfig {
  public_name: string;
  text_color: string | null;
  color: string | null;
  color2: string | null;
  gradient: boolean;
  mode: "full" | "button";
  style: "solid" | "glass";
  logo_url: string | null;
  logo_scale: number | null;
  icon: string;
  position: string;
  // When true, this agent accepts an unverified, client-generated visitor
  // id instead of requiring real end-user identity (HMAC/JWT) — set per
  // agent for public/anonymous sites with no logged-in users. See
  // index.ts's use of this alongside getOrCreateAnonymousVisitorId().
  public_identity: boolean;
  // Tenant-authored text (emoji allowed), per language, shown in a speech
  // bubble beside the widget bubble to draw a visitor's attention to it --
  // an empty object means the feature is off. Which trigger(s) below
  // actually fire it is independent of whether any message is set. Only
  // languages the tenant configured appear as keys (e.g. "en"/"es"/"pt").
  attention_messages: Record<string, string>;
  // Same language keys as attention_messages, but only present for a
  // language where a voice was also configured and ElevenLabs generation
  // has succeeded at least once -- a language can have text with no audio
  // yet (shows the bubble, stays silent).
  attention_audio_urls: Record<string, string>;
  attention_trigger_first_visit: boolean;
  attention_trigger_idle: boolean;
  // Which side of the bubble the message renders on, and how wide --
  // independent of `position` (the bubble's own screen corner). Always
  // present (server-side defaults: "top" / 220), unlike attention_message.
  attention_position: string;
  attention_width: number;
  // Gates the analytics-capture module (pageviews/clicks) -- see
  // analytics.ts. Per agent, same opt-in idea as public_identity.
  analytics_enabled: boolean;
  // Only meaningful when analytics_enabled is true: capture nothing until
  // the host page calls window.ArtisWidget.grantAnalyticsConsent().
  analytics_wait_for_consent: boolean;
  // Task D4/D4a -- every currently-`merged` Autofix fix to probe for on a
  // matching page. Always empty when analytics_enabled is false: the
  // probe event rides the same analytics module/consent gate as every
  // other event (see autofixProbe.ts), so there's nothing to send it
  // through without that module existing at all.
  autofix_probes: AutofixProbeEntry[];
  // Task G1 -- a SEPARATE opt-in from analytics_enabled, but only takes
  // effect when analytics_enabled is ALSO true: the backend_error event
  // rides the same AnalyticsModule/consent gate as every other event (see
  // backendErrorCapture.ts), same dependency chain autofix_probes already
  // has above.
  backend_error_capture_enabled: boolean;
}

export interface AutofixProbeEntry {
  fix_id: string;
  path: string;
  selector: string;
}

export async function fetchWidgetConfig(
  apiUrl: string,
  agentId: string,
): Promise<WidgetConfig | null> {
  try {
    const res = await fetch(
      `${apiUrl}/ai-service/api/v1/agents/${agentId}/widget-config`,
    );
    if (!res.ok) return null;
    return (await res.json()) as WidgetConfig;
  } catch {
    return null;
  }
}

/** Powers the "first visit" attention trigger -- true when this
 * end_user_id has never had a conversation with this agent before.
 * Unauthenticated, same reasoning as fetchWidgetConfig: called before any
 * conversation exists yet. Fails closed (false) on any error, same as a
 * trigger that just never fires rather than one that misfires. */
export async function checkIsFirstVisit(
  apiUrl: string,
  agentId: string,
  endUserId: string,
): Promise<boolean> {
  try {
    const res = await fetch(
      `${apiUrl}/ai-service/api/v1/agents/${agentId}/visitor-status?end_user_id=${encodeURIComponent(endUserId)}`,
    );
    if (!res.ok) return false;
    const data = (await res.json()) as { is_new: boolean };
    return data.is_new;
  } catch {
    return false;
  }
}

/** Reassigns every analytics event recorded under an anonymous id to the
 * real visitor id, once one resolves partway through a session that already
 * had anon-tagged events — see analytics.ts. Fire-and-forget: a lost merge
 * just means that session's early events stay under the anon id, which is
 * no worse than today's baseline (nothing merges at all). */
export function mergeAnalyticsVisitor(
  apiUrl: string,
  agentId: string,
  anonVisitorId: string,
  realVisitorId: string,
): void {
  fetch(`${apiUrl}/ai-service/api/v1/widget/events/merge-visitor`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      agent_id: agentId,
      anon_visitor_id: anonVisitorId,
      real_visitor_id: realVisitorId,
    }),
    keepalive: true,
  }).catch(() => {});
}

export interface WidgetConversationResult {
  conversationId: string;
  // Claim_path-resolved identity fields (AgentIdentityField, Install tab),
  // verified server-side from this visitor's JWT -- null for the
  // HMAC/public/anonymous identity paths, which never resolve any.
  identityExtra: Record<string, string> | null;
  // See WidgetIdentifyResult.endUserId's own comment -- null for the
  // public/anonymous paths, which never resolve a real id either.
  endUserId: string | null;
}

/** Resolves claim_path identity fields from a JWT as its own event --
 * independent of whether a conversation ever gets created, same timing
 * as PostHog/Intercom/Segment's own identify(). Fire-and-forget by
 * design, same as mergeAnalyticsVisitor: a failed/slow resolve just
 * means those fields stay missing a bit longer, never blocks anything. */
export interface WidgetIdentifyResult {
  identityExtra: Record<string, string> | null;
  // The resolved real visitor id -- lets the widget start tagging
  // subsequent analytics events with it instead of the anon id a JWT
  // identity otherwise has to ride under client-side (see
  // analytics.ts's own resolveVisitorId comment).
  endUserId: string | null;
}

export async function identifyWidgetVisitor(
  apiUrl: string,
  agentId: string,
  idToken: string,
): Promise<WidgetIdentifyResult | null> {
  try {
    const res = await fetch(`${apiUrl}/ai-service/api/v1/widget/identify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ agent_id: agentId, id_token: idToken }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      identity_extra: Record<string, string> | null;
      end_user_id: string | null;
    };
    return { identityExtra: data.identity_extra ?? null, endUserId: data.end_user_id ?? null };
  } catch {
    return null;
  }
}

export async function createWidgetConversation(
  apiUrl: string,
  agentId: string,
  identity: Identity,
  language?: string,
): Promise<WidgetConversationResult> {
  const res = await fetch(`${apiUrl}/ai-service/api/v1/widget/conversations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      agent_id: agentId,
      end_user_id: identity.endUserId ?? null,
      user_hash: identity.userHash ?? null,
      id_token: identity.idToken ?? null,
      // Descriptive only — never used for access control, only
      // end_user_id/user_hash (or id_token) above is verified. Same
      // convention Intercom uses for identify() attributes.
      user_metadata: identity.metadata ?? null,
      // The visitor's detected browser language (see detectVisitorLanguage
      // in index.ts) -- lets this one call start already in that language
      // instead of the agent's default "auto, match the caller".
      language: language ?? null,
    }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ArtisApiError(`Could not start conversation (${res.status}): ${body}`);
  }
  const data = (await res.json()) as {
    conversation_id: string;
    identity_extra: Record<string, string> | null;
    end_user_id: string | null;
  };
  return {
    conversationId: data.conversation_id,
    identityExtra: data.identity_extra ?? null,
    endUserId: data.end_user_id ?? null,
  };
}
